<template>
    <div>
        <!-- Not connected state -->
        <div v-if="!isConnected" class="compass-card text-center py-5">
            <i class="fas fa-wallet mb-3" style="font-size: 3rem; color: #6b7280;"></i>
            <h3 style="font-weight: 600; color: #e5e7eb;">Connect Your Wallet</h3>
            <p style="color: #9ca3af; max-width: 500px; margin: 0 auto 1.5rem;">
                Connect your Solana wallet to view your DeFi positions, track performance, and manage your portfolio across all protocols.
            </p>
            <wallet-button></wallet-button>
        </div>

        <!-- Loading state -->
        <div v-else-if="loading && !report" class="compass-card text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p style="color: #9ca3af;">Loading portfolio from Solana...</p>
            <p style="color: #6b7280; font-size: 0.85rem;">
                Fetching positions across staking, lending, liquidity, earn, and prediction protocols
            </p>
        </div>

        <!-- Portfolio loaded -->
        <div v-else-if="report">
            <!-- Total Value + Actions -->
            <div class="row mb-4">
                <div class="col-md-4 col-12 mb-3">
                    <div class="compass-stat-card accent-top">
                        <div class="stat-label">Total Portfolio Value</div>
                        <div class="stat-value font-mono-num">${{ formatUsd(report.totalValueUsd) }}</div>
                        <div v-if="pnlData" class="stat-unit">
                            <span :class="pnlData.totalChange >= 0 ? 'pct-positive' : 'pct-negative'">
                                {{ pnlData.totalChange >= 0 ? '+' : '' }}${{ formatUsd(Math.abs(pnlData.totalChange)) }}
                                <span v-if="pnlData.totalChangePct !== null">
                                    ({{ pnlData.totalChange >= 0 ? '+' : '' }}{{ pnlData.totalChangePct.toFixed(1) }}%)
                                </span>
                            </span>
                            <span style="color: #6b7280;"> since snapshot</span>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 col-6 mb-3">
                    <div class="compass-stat-card accent-top">
                        <div class="stat-label">Positions</div>
                        <div class="stat-value font-mono-num">{{ report.positions.length }}</div>
                        <div class="stat-unit">Across {{ uniqueProtocols.length }} protocol{{ uniqueProtocols.length !== 1 ? 's' : '' }}</div>
                    </div>
                </div>
                <div class="col-md-4 col-6 mb-3">
                    <div class="compass-stat-card accent-top">
                        <div class="stat-label">Claimable MEV</div>
                        <div class="stat-value font-mono-num">{{ report.claimableMev > 0 ? report.claimableMev.toFixed(4) + ' SOL' : '—' }}</div>
                        <div class="stat-unit">
                            <a v-if="report.claimableMev > 0" href="/app/stake" style="color: var(--accent-primary);">Claim rewards &rarr;</a>
                            <span v-else>No unclaimed rewards</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Charts Row -->
            <div class="row mb-4">
                <div class="col-md-5 mb-3">
                    <div class="compass-card h-100">
                        <h5 style="font-weight: 600; font-size: 1rem;" class="mb-3">Allocation</h5>
                        <div v-if="allocationData.length > 0" style="max-width: 280px; margin: 0 auto;">
                            <canvas ref="allocationChart"></canvas>
                        </div>
                        <div v-else class="text-center py-4" style="color: #6b7280;">
                            No allocation data
                        </div>
                    </div>
                </div>
                <div class="col-md-7 mb-3">
                    <div class="compass-card h-100">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h5 style="font-weight: 600; font-size: 1rem;" class="mb-0">Value by Category</h5>
                            <div class="d-flex gap-2">
                                <button
                                    class="compass-btn compass-btn--sm"
                                    :class="snapshotLoading ? 'compass-btn--outline' : 'compass-btn--primary'"
                                    :disabled="snapshotLoading"
                                    @click="takeSnapshot"
                                >
                                    <i class="fas fa-camera me-1"></i>
                                    {{ snapshotLoading ? 'Saving...' : 'Snapshot' }}
                                </button>
                                <button
                                    v-if="hasSnapshots"
                                    class="compass-btn compass-btn--sm compass-btn--outline"
                                    :disabled="pnlLoading"
                                    @click="compareToPrevious"
                                >
                                    <i class="fas fa-chart-line me-1"></i>
                                    {{ pnlLoading ? 'Comparing...' : 'Compare' }}
                                </button>
                            </div>
                        </div>
                        <div v-if="categoryBreakdown.length > 0">
                            <canvas ref="categoryChart"></canvas>
                        </div>
                        <div v-else class="text-center py-4" style="color: #6b7280;">
                            No category data
                        </div>
                    </div>
                </div>
            </div>

            <!-- Snapshot Info -->
            <div v-if="report.lastSnapshot" class="compass-card mb-4" style="padding: 0.75rem 1rem; background: rgba(139, 92, 246, 0.05);">
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span style="color: #9ca3af; font-size: 0.85rem;">
                        <i class="fas fa-history me-1"></i>
                        Last snapshot: {{ report.lastSnapshot.ago }}
                        <span v-if="report.lastSnapshot.label"> &mdash; {{ report.lastSnapshot.label }}</span>
                    </span>
                    <span v-if="snapshotMessage" style="font-size: 0.85rem;" :class="snapshotMessage.type === 'success' ? 'pct-positive' : 'pct-negative'">
                        {{ snapshotMessage.text }}
                    </span>
                </div>
            </div>

            <!-- Category Tabs -->
            <div class="compass-card">
                <div class="d-flex gap-2 mb-3 flex-wrap">
                    <button
                        v-for="tab in availableTabs"
                        :key="tab.key"
                        class="compass-btn compass-btn--sm"
                        :class="activeTab === tab.key ? 'compass-btn--primary' : 'compass-btn--outline'"
                        @click="activeTab = tab.key"
                    >
                        {{ tab.label }}
                        <span v-if="tab.count > 0" class="ms-1" style="opacity: 0.7;">({{ tab.count }})</span>
                    </button>
                </div>

                <!-- Positions Table -->
                <div class="table-mobile-scroll">
                    <table class="compass-table" v-if="filteredPositions.length > 0">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Protocol</th>
                                <th>Asset</th>
                                <th class="text-end">Amount</th>
                                <th class="text-end">Value</th>
                                <th v-if="pnlData" class="text-end">P&L</th>
                                <th class="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(pos, idx) in filteredPositions" :key="idx">
                                <td>
                                    <span class="badge-category" :class="'badge-' + pos.type">
                                        {{ categoryLabel(pos.type) }}
                                    </span>
                                </td>
                                <td>{{ protocolLabel(pos.protocol) }}</td>
                                <td>
                                    <strong>{{ pos.symbol || 'Unknown' }}</strong>
                                </td>
                                <td class="text-end td-num font-mono-num">{{ formatAmount(pos.amount) }}</td>
                                <td class="text-end td-num font-mono-num">
                                    {{ pos.valueUsd !== null ? '$' + formatUsd(pos.valueUsd) : '—' }}
                                </td>
                                <td v-if="pnlData" class="text-end td-num">
                                    <template v-if="pnlLookup[pos.symbol + ':' + pos.wallet]">
                                        <span :class="pnlLookup[pos.symbol + ':' + pos.wallet].change >= 0 ? 'pct-positive' : 'pct-negative'">
                                            {{ pnlLookup[pos.symbol + ':' + pos.wallet].change >= 0 ? '+' : '' }}${{ formatUsd(Math.abs(pnlLookup[pos.symbol + ':' + pos.wallet].change)) }}
                                        </span>
                                    </template>
                                    <span v-else style="color: #6b7280;">—</span>
                                </td>
                                <td class="text-end">
                                    <a :href="manageLinkForPosition(pos)" class="compass-btn compass-btn--sm compass-btn--outline">
                                        Manage
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot v-if="filteredPositions.length > 0">
                            <tr style="font-weight: 600;">
                                <td colspan="4">Total</td>
                                <td class="text-end td-num font-mono-num">
                                    ${{ formatUsd(filteredTotal) }}
                                </td>
                                <td v-if="pnlData"></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                    <div v-else class="text-center py-4" style="color: #6b7280;">
                        <p class="mb-2">No {{ activeTab === 'all' ? '' : categoryLabel(activeTab) + ' ' }}positions found.</p>
                        <a v-if="activeTab === 'all'" href="/app/stake" class="compass-btn compass-btn--sm compass-btn--primary">
                            Start by Staking SOL
                        </a>
                    </div>
                </div>
            </div>

            <!-- P&L Comparison Detail -->
            <div v-if="pnlData" class="compass-card mt-4">
                <h5 style="font-weight: 600; font-size: 1rem;" class="mb-3">
                    <i class="fas fa-chart-line me-1"></i> P&L Since Snapshot
                    <span v-if="pnlData.snapshotDate" style="color: #6b7280; font-size: 0.85rem; font-weight: 400;">
                        ({{ pnlData.snapshotDate }})
                    </span>
                </h5>
                <div class="row mb-3">
                    <div class="col-md-4 col-6">
                        <div class="stat-label">Before</div>
                        <div class="stat-value font-mono-num" style="font-size: 1.1rem;">${{ formatUsd(pnlData.totalBefore) }}</div>
                    </div>
                    <div class="col-md-4 col-6">
                        <div class="stat-label">After</div>
                        <div class="stat-value font-mono-num" style="font-size: 1.1rem;">${{ formatUsd(pnlData.totalAfter) }}</div>
                    </div>
                    <div class="col-md-4 col-12 mt-2 mt-md-0">
                        <div class="stat-label">Change</div>
                        <div class="stat-value font-mono-num" style="font-size: 1.1rem;"
                             :class="pnlData.totalChange >= 0 ? 'pct-positive' : 'pct-negative'">
                            {{ pnlData.totalChange >= 0 ? '+' : '' }}${{ formatUsd(Math.abs(pnlData.totalChange)) }}
                            <span v-if="pnlData.totalChangePct !== null" style="font-size: 0.85rem;">
                                ({{ pnlData.totalChangePct >= 0 ? '+' : '' }}{{ pnlData.totalChangePct.toFixed(2) }}%)
                            </span>
                        </div>
                    </div>
                </div>
                <div class="table-mobile-scroll" v-if="pnlData.diffs && pnlData.diffs.length > 0">
                    <table class="compass-table">
                        <thead>
                            <tr>
                                <th>Asset</th>
                                <th class="text-end">Before</th>
                                <th class="text-end">After</th>
                                <th class="text-end">Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="diff in pnlData.diffs" :key="diff.symbol + diff.wallet">
                                <td><strong>{{ diff.symbol }}</strong></td>
                                <td class="text-end td-num font-mono-num">${{ formatUsd(diff.valueBefore) }}</td>
                                <td class="text-end td-num font-mono-num">${{ formatUsd(diff.valueAfter) }}</td>
                                <td class="text-end td-num font-mono-num"
                                    :class="diff.change >= 0 ? 'pct-positive' : 'pct-negative'">
                                    {{ diff.change >= 0 ? '+' : '' }}${{ formatUsd(Math.abs(diff.change)) }}
                                    <span v-if="diff.changePct !== null" style="font-size: 0.8rem; opacity: 0.8;">
                                        ({{ diff.changePct >= 0 ? '+' : '' }}{{ diff.changePct.toFixed(1) }}%)
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="compass-card text-center py-4">
            <i class="fas fa-exclamation-triangle mb-3" style="font-size: 2rem; color: #ef4444;"></i>
            <p style="color: #ef4444;">{{ error }}</p>
            <button class="compass-btn compass-btn--sm compass-btn--primary" @click="loadPortfolio">
                <i class="fas fa-redo me-1"></i> Retry
            </button>
        </div>

        <!-- Fallback: connected but no data yet (e.g. SDK failed to initialize) -->
        <div v-else class="compass-card text-center py-4">
            <i class="fas fa-wallet mb-3" style="font-size: 2rem; color: #6b7280;"></i>
            <p style="color: #9ca3af;">Wallet connected but portfolio data could not be loaded.</p>
            <button class="compass-btn compass-btn--sm compass-btn--primary" @click="loadPortfolio">
                <i class="fas fa-redo me-1"></i> Load Portfolio
            </button>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import axios from 'axios';
import { useSolSdk } from '../composables/useSolSdk';
import { formatCompact } from '../composables/useFormatters';
import { colors as chartColors, getDoughnutChartOptions, getBarChartOptions } from '../composables/useChartConfig';

const PROTOCOL_LABELS = {
    'kamino': 'Kamino',
    'drift': 'Drift',
    'marginfi': 'MarginFi',
    'jup-lend': 'Jupiter',
    'loopscale': 'Loopscale',
    'orca': 'Orca',
    'raydium': 'Raydium',
    'meteora': 'Meteora',
    'native': 'Native',
    'jupiter': 'Jupiter',
};

const CATEGORY_LABELS = {
    stake: 'Staking',
    lend: 'Lending',
    lp: 'Liquidity',
    earn: 'Earn',
    token: 'Tokens',
    predict: 'Predict',
    order: 'Orders',
};

const TYPE_TO_CATEGORY = {
    token: 'token',
    stake: 'staking',
    lend: 'lending',
    lp: 'liquidity',
    order: 'orders',
    predict: 'predictions',
    earn: 'earn',
};

const CATEGORY_COLORS = {
    'Staking': '#8b5cf6',
    'Lending': '#3b82f6',
    'Liquidity': '#06b6d4',
    'Earn': '#22c55e',
    'Tokens': '#f59e0b',
    'Predict': '#ec4899',
    'Orders': '#ef4444',
};

export default {
    name: 'PortfolioOverview',
    setup() {
        const { getSdk } = useSolSdk();

        const report = ref(null);
        const pnlData = ref(null);
        const loading = ref(false);
        const error = ref(null);
        const activeTab = ref('all');
        const snapshotLoading = ref(false);
        const snapshotMessage = ref(null);
        const pnlLoading = ref(false);

        // Reactive wallet tracking — window.wallet.publicKey is a Vue Ref
        // but may not exist when the component mounts. We use a local ref
        // and set up a watch once the wallet adapter initializes.
        const walletConnected = ref(!!window.wallet?.publicKey?.value);

        const isConnected = computed(() => walletConnected.value);

        function getWalletAddress() {
            return window.wallet?.publicKey?.value?.toBase58() || null;
        }

        function getWallets() {
            const addr = getWalletAddress();
            if (!addr) return [];
            return [{ name: 'main', address: addr }];
        }

        function onWalletChange(newVal) {
            walletConnected.value = !!newVal;
            if (newVal) loadPortfolio();
            else { report.value = null; pnlData.value = null; }
        }

        function startWalletWatch() {
            walletConnected.value = !!window.wallet.publicKey.value;
            watch(() => window.wallet.publicKey.value, onWalletChange);
            if (walletConnected.value) loadPortfolio();
        }

        function setupWalletWatch() {
            if (window.wallet?.publicKey) {
                startWalletWatch();
            } else {
                // Wallet adapter not yet initialized — retry after a tick
                setTimeout(() => {
                    if (window.wallet?.publicKey) startWalletWatch();
                }, 500);
            }
        }

        // --- Load portfolio from SDK ---
        async function loadPortfolio() {
            const wallets = getWallets();
            if (wallets.length === 0) return;

            loading.value = true;
            error.value = null;

            try {
                const sdk = await getSdk();
                const result = await sdk.portfolio.get(wallets);
                report.value = result;

                // Auto-snapshot on load (fire-and-forget)
                sdk.portfolio.autoSnapshot(result).catch(e =>
                    console.debug('[Portfolio] Auto-snapshot skipped:', e.message)
                );

                // Persist to backend for historical tracking (fire-and-forget)
                syncToBackend(wallets[0].address, result.positions);
            } catch (e) {
                console.error('[Portfolio] Failed to load:', e);
                error.value = 'Failed to load portfolio. Please try again.';
            } finally {
                loading.value = false;
            }
        }

        async function syncToBackend(address, positions) {
            try {
                const mapped = positions.map(p => ({
                    category: TYPE_TO_CATEGORY[p.type] || p.type,
                    protocol: p.protocol || null,
                    mint: p.mint || null,
                    symbol: p.symbol || null,
                    amount: p.amount,
                    valueUsd: p.valueUsd,
                    extra: p.extra || null,
                }));
                await axios.post(`/api/portfolio/${address}/sync`, { positions: mapped });
            } catch (e) {
                console.debug('[Portfolio] Backend sync skipped:', e.message);
            }
        }

        // --- Snapshots ---
        const hasSnapshots = computed(() => !!report.value?.lastSnapshot);

        async function takeSnapshot() {
            snapshotLoading.value = true;
            snapshotMessage.value = null;
            try {
                const sdk = await getSdk();
                const result = await sdk.portfolio.takeSnapshot(getWallets());
                snapshotMessage.value = {
                    type: 'success',
                    text: `Snapshot saved ($${formatUsd(result.totalValueUsd)})`,
                };
                // Refresh to update lastSnapshot metadata
                await loadPortfolio();
            } catch (e) {
                snapshotMessage.value = { type: 'error', text: 'Snapshot failed: ' + e.message };
            } finally {
                snapshotLoading.value = false;
            }
        }

        async function compareToPrevious() {
            pnlLoading.value = true;
            try {
                const sdk = await getSdk();
                const result = await sdk.portfolio.getPnl(getWallets());
                pnlData.value = result;
            } catch (e) {
                snapshotMessage.value = { type: 'error', text: 'Compare failed: ' + e.message };
            } finally {
                pnlLoading.value = false;
            }
        }

        // --- Computed ---
        const uniqueProtocols = computed(() => {
            if (!report.value) return [];
            const protos = new Set();
            report.value.positions.forEach(p => {
                if (p.protocol) protos.add(p.protocol);
            });
            return [...protos];
        });

        const allocationData = computed(() => report.value?.allocation || []);

        const categoryBreakdown = computed(() => {
            if (!report.value) return [];
            const cats = {};
            report.value.positions.forEach(p => {
                const cat = categoryLabel(p.type);
                cats[cat] = (cats[cat] || 0) + (p.valueUsd || 0);
            });
            return Object.entries(cats)
                .map(([label, value]) => ({ label, value }))
                .sort((a, b) => b.value - a.value);
        });

        const availableTabs = computed(() => {
            const tabs = [{ key: 'all', label: 'All', count: report.value?.positions?.length || 0 }];
            const catCounts = {};
            (report.value?.positions || []).forEach(p => {
                catCounts[p.type] = (catCounts[p.type] || 0) + 1;
            });
            const order = ['stake', 'lend', 'lp', 'earn', 'token', 'predict', 'order'];
            order.forEach(key => {
                if (catCounts[key]) {
                    tabs.push({ key, label: categoryLabel(key), count: catCounts[key] });
                }
            });
            return tabs;
        });

        const filteredPositions = computed(() => {
            if (!report.value) return [];
            if (activeTab.value === 'all') return report.value.positions;
            return report.value.positions.filter(p => p.type === activeTab.value);
        });

        const filteredTotal = computed(() =>
            filteredPositions.value.reduce((sum, p) => sum + (p.valueUsd || 0), 0)
        );

        // Pre-build a lookup map for O(1) P&L access per position row
        const pnlLookup = computed(() => {
            if (!pnlData.value?.diffs) return {};
            const map = {};
            pnlData.value.diffs.forEach(d => {
                map[d.symbol + ':' + d.wallet] = d;
            });
            return map;
        });

        // --- Formatters ---
        function formatUsd(value) {
            if (value == null || isNaN(value)) return '0.00';
            if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(2) + 'M';
            if (Math.abs(value) >= 1_000) return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return value.toFixed(2);
        }

        function formatAmount(value) {
            if (value == null || isNaN(value)) return '0';
            if (value >= 1_000_000) return formatCompact(value);
            if (value >= 1_000) return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
            if (value >= 1) return value.toFixed(4);
            if (value >= 0.0001) return value.toFixed(6);
            return value.toExponential(2);
        }

        function categoryLabel(type) {
            return CATEGORY_LABELS[type] || type;
        }

        function protocolLabel(protocol) {
            if (!protocol) return '—';
            return PROTOCOL_LABELS[protocol] || protocol.charAt(0).toUpperCase() + protocol.slice(1);
        }

        function manageLinkForPosition(pos) {
            const links = {
                stake: '/app/stake',
                lend: '/app/borrow',
                lp: '/app/liquidity',
                earn: '/app/earn',
                predict: '/app/predict',
                order: '/app/trade',
                token: '/tokens/' + (pos.symbol || '').toLowerCase(),
            };
            return links[pos.type] || '/app';
        }

        // --- Charts ---
        const allocationChart = ref(null);
        const categoryChart = ref(null);
        let chartInstances = { allocation: null, category: null };

        async function renderCharts() {
            await nextTick();
            const Chart = (await import('chart.js/auto')).default;

            // Allocation Doughnut
            if (allocationChart.value && allocationData.value.length > 0) {
                if (chartInstances.allocation) chartInstances.allocation.destroy();

                const doughnutOpts = getDoughnutChartOptions({ showLegend: true, legendPosition: 'bottom' });

                chartInstances.allocation = new Chart(allocationChart.value, {
                    type: 'doughnut',
                    data: {
                        labels: allocationData.value.map(a => a.symbol),
                        datasets: [{
                            data: allocationData.value.map(a => a.pct),
                            backgroundColor: chartColors.series.slice(0, allocationData.value.length),
                            borderWidth: 0,
                            hoverOffset: 4,
                        }],
                    },
                    options: {
                        ...doughnutOpts,
                        maintainAspectRatio: true,
                        cutout: '65%',
                        plugins: {
                            ...doughnutOpts.plugins,
                            legend: {
                                ...doughnutOpts.plugins?.legend,
                                position: 'bottom',
                                labels: {
                                    ...doughnutOpts.plugins?.legend?.labels,
                                    padding: 12,
                                    font: { size: 11 },
                                    usePointStyle: true,
                                    generateLabels(chart) {
                                        const data = chart.data;
                                        return data.labels.map((label, i) => ({
                                            text: `${label} ${data.datasets[0].data[i].toFixed(1)}%`,
                                            fillStyle: data.datasets[0].backgroundColor[i],
                                            strokeStyle: 'transparent',
                                            pointStyle: 'circle',
                                            index: i,
                                        }));
                                    },
                                },
                            },
                            tooltip: {
                                callbacks: {
                                    label(ctx) {
                                        const entry = allocationData.value[ctx.dataIndex];
                                        return `${entry.symbol}: ${entry.pct.toFixed(1)}% ($${formatUsd(entry.valueUsd)})`;
                                    },
                                },
                            },
                        },
                    },
                });
            }

            // Category Bar Chart
            if (categoryChart.value && categoryBreakdown.value.length > 0) {
                if (chartInstances.category) chartInstances.category.destroy();

                const barOpts = getBarChartOptions({
                    horizontal: true,
                    showLegend: false,
                    formatCallback: (v) => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v),
                });

                chartInstances.category = new Chart(categoryChart.value, {
                    type: 'bar',
                    data: {
                        labels: categoryBreakdown.value.map(c => c.label),
                        datasets: [{
                            data: categoryBreakdown.value.map(c => c.value),
                            backgroundColor: categoryBreakdown.value.map(c => CATEGORY_COLORS[c.label] || '#6b7280'),
                            borderRadius: 6,
                            barThickness: 36,
                        }],
                    },
                    options: {
                        ...barOpts,
                        maintainAspectRatio: false,
                        plugins: {
                            ...barOpts.plugins,
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => '$' + formatUsd(ctx.parsed.x),
                                },
                            },
                        },
                    },
                });
            }
        }

        function destroyCharts() {
            if (chartInstances.allocation) chartInstances.allocation.destroy();
            if (chartInstances.category) chartInstances.category.destroy();
            chartInstances = { allocation: null, category: null };
        }

        // --- Lifecycle ---
        onMounted(() => {
            if (isConnected.value) {
                loadPortfolio();
            }
            setupWalletWatch();
        });

        onBeforeUnmount(destroyCharts);

        watch(report, () => {
            if (report.value) renderCharts();
        });

        return {
            report, pnlData, loading, error, activeTab,
            snapshotLoading, snapshotMessage, pnlLoading, isConnected,
            uniqueProtocols, allocationData, categoryBreakdown,
            availableTabs, filteredPositions, filteredTotal,
            hasSnapshots, pnlLookup,
            loadPortfolio, takeSnapshot, compareToPrevious,
            formatUsd, formatAmount, categoryLabel, protocolLabel,
            manageLinkForPosition,
            allocationChart, categoryChart,
        };
    },
};
</script>

<style scoped>
.badge-category {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.badge-stake { background: rgba(139, 92, 246, 0.15); color: #a78bfa; }
.badge-lend { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
.badge-lp { background: rgba(6, 182, 212, 0.15); color: #22d3ee; }
.badge-earn { background: rgba(34, 197, 94, 0.15); color: #4ade80; }
.badge-token { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
.badge-predict { background: rgba(236, 72, 153, 0.15); color: #f472b6; }
.badge-order { background: rgba(239, 68, 68, 0.15); color: #f87171; }
</style>
